<script setup lang="ts">
import { computed, nextTick, ref } from "vue";
import { useTranslation } from "i18next-vue";
import { useQueryClient } from "@tanstack/vue-query";
import {
  useImageFiles,
  useUpdateFindingById,
  useDeleteFinding,
  useMoveFinding,
} from "@/stores/queries";
import { useAddManyReportImagesToFinding } from "@/stores/queries/report-images.query";
import { QueryKey } from "@/stores/queries/query-keys";
import type { Finding } from "@/types/findings.types";
import { RSFindingsColumns } from "@/types/report-settings.types";
import { useFindingsTableColumns } from "@/composable/useFindingsTableColumns";
import { useReportNotationSystem } from "@/composable/useReportNotationSystem";
import DisorderGradeBadge from "@/components/UI/DisorderGradeBadge.vue";
import VisualiseImage from "./VisualiseImage.vue";
import DRBtnIcon from "@/components/UI/DRBtnIcon.vue";
import DRBtn from "@/components/UI/DRBtn.vue";
import GaleryModal from "../../galery/GaleryModal.vue";
import FindingActionsDropdown from "./FindingActionsDropdown.vue";

const { t } = useTranslation();
const { visibleColumns } = useFindingsTableColumns();

// Custom directive for autofocus
const vFocus = {
  mounted: (el: HTMLElement) => {
    nextTick(() => {
      const input = el.querySelector("input, textarea") as HTMLElement;
      input?.focus();
    });
  },
};

const queryClient = useQueryClient();

const props = defineProps<{
  findings: Finding[];
  diagnosticId: number;
  reportId: number;
  sectionId?: number | null;
  showAddButton?: boolean;
}>();

const emit = defineEmits<{
  (e: "edit", finding: Finding): void;
  (e: "create"): void;
}>();

// Delete finding mutation
const { mutateAsync: deleteFinding, isPending: isDeleting } =
  useDeleteFinding();

// Move finding mutation
const { mutateAsync: moveFinding } = useMoveFinding();

const handleDeleteFinding = async (finding: Finding) => {
  await deleteFinding(finding.id);
};

const handleMoveFinding = async (
  finding: Finding,
  newSectionId: number | null,
) => {
  await moveFinding({
    id: finding.id,
    oldSectionId: finding.sectionId ?? null,
    newSectionId,
    reportId: props.reportId,
  });
};

const reportIdRef = computed(() => props.reportId);

// Les notes proposées suivent le système mémorisé pour ce rapport, choisi dans
// le formulaire de constatation. En notation libre, la cellule devient un champ
// de saisie : une liste de notes prédéfinies n'aurait pas de sens.
const { isCustom: isCustomNotation, gradeOptions: notationOptions } =
  useReportNotationSystem(reportIdRef);

// Inline editing state
const editingId = ref<number | null>(null);
const editingField = ref<string | null>(null);

const startEditing = (id: number, field: string) => {
  editingId.value = id;
  editingField.value = field;
};

const stopEditing = () => {
  // Delay reset so that a row-click triggered by the blur is still blocked.
  // Capture the current edit session so we don't clobber a new one started
  // in the meantime (e.g. user clicked directly on another editable cell).
  const id = editingId.value;
  const field = editingField.value;
  setTimeout(() => {
    if (editingId.value === id && editingField.value === field) {
      editingId.value = null;
      editingField.value = null;
    }
  }, 100);
};

const isEditing = (id: number, field: string) =>
  editingId.value === id && editingField.value === field;

// Update finding mutation
const { mutate: updateFinding } = useUpdateFindingById();

const handleUpdate = (finding: Finding, field: keyof Finding, value: any) => {
  updateFinding({ id: finding.id, payload: { [field]: value } });
  stopEditing();
};

const handleRowClick = (row: Finding) => {
  if (editingId.value !== null) return;
  emit("edit", row);
};

// Image viewer state
const viewerImages = ref<string[]>([]);
const viewerInitialIndex = ref(0);
const showViewer = ref(false);

// Gallery modal state
const showGallery = ref(false);
const selectedFindingForGallery = ref<Finding | null>(null);

// Add images mutation (batch) - uses ReportImage
const { mutateAsync: addImagesToFinding, isPending: isAddingImage } =
  useAddManyReportImagesToFinding(reportIdRef);

// Collect all fileIds from all findings for batch loading
const allFileIds = computed(() => {
  if (!props.findings) return [];
  const ids: number[] = [];
  props.findings.forEach((f) => {
    f.reportImages?.forEach((img) => {
      if (img.diagnosticImage?.fileId) {
        ids.push(img.diagnosticImage.fileId);
      }
    });
  });
  return ids;
});

const { data: allImageUrls } = useImageFiles(allFileIds);

// Get image URLs for a specific finding
const getImageUrls = (finding: Finding): string[] => {
  if (!finding.reportImages?.length || !allImageUrls.value) return [];
  return finding.reportImages
    .map((img) => {
      const fileId = img.diagnosticImage?.fileId;
      if (!fileId) return null;
      const index = allFileIds.value.indexOf(fileId);
      return index >= 0 ? allImageUrls.value[index] : null;
    })
    .filter((url): url is string => !!url);
};

// Open image viewer for a finding
const openImageViewer = (finding: Finding, imageIndex: number = 0) => {
  const urls = getImageUrls(finding);
  if (urls.length > 0) {
    viewerImages.value = urls;
    viewerInitialIndex.value = imageIndex;
    showViewer.value = true;
  }
};

// Open gallery to add image
const openGalleryForFinding = (finding: Finding) => {
  selectedFindingForGallery.value = finding;
  showGallery.value = true;
};

// Handle adding images from gallery (supports batch)
const handleAddImages = async (reportImageIds: number[]) => {
  if (!selectedFindingForGallery.value || reportImageIds.length === 0) return;
  await addImagesToFinding({
    findingId: selectedFindingForGallery.value.id,
    imageIds: reportImageIds,
  });
  // Invalidate queries
  if (props.sectionId) {
    queryClient.invalidateQueries({
      queryKey: [QueryKey.FINDINGS, { section: props.sectionId }],
    });
  } else {
    queryClient.invalidateQueries({
      queryKey: [QueryKey.FINDINGS, { unsorted: props.reportId }],
    });
  }
  showGallery.value = false;
  selectedFindingForGallery.value = null;
};
</script>

<template>
  <div class="bordered rounded">
    <el-table
      :data="findings"
      size="large"
      style="width: 100%"
      row-key="id"
      class="clickable-rows"
      @row-click="handleRowClick"
    >
      <template v-for="col in visibleColumns" :key="col">
        <!-- Notation column -->
        <el-table-column
          v-if="col === RSFindingsColumns.NOTATION"
          :label="t('report:diagnostics.finding.shortLabel.notation')"
          width="100"
        >
          <template #default="{ row }">
            <el-input
              v-if="isCustomNotation"
              :model-value="row.notation ?? ''"
              size="small"
              :placeholder="
                t('report:diagnostics.finding.item.notationCustomPlaceholder')
              "
              @click.stop
              @change="
                (val: string) =>
                  handleUpdate(row, 'notation', val.trim() ? val.trim() : null)
              "
            />
            <el-dropdown
              v-else
              trigger="click"
              @command="
                (val: string | undefined) =>
                  handleUpdate(row, 'notation', val === 'null' ? null : val)
              "
            >
              <div class="clickable-cell mt-1" @click.stop>
                <DisorderGradeBadge :value="row.notation" size="md" />
              </div>
              <template #dropdown>
                <el-dropdown-menu>
                  <el-dropdown-item command="null">
                    <DisorderGradeBadge :value="null" size="sm" />
                  </el-dropdown-item>
                  <el-dropdown-item
                    v-for="value in notationOptions"
                    :key="value"
                    :command="value"
                  >
                    <DisorderGradeBadge :value="value" size="sm" />
                  </el-dropdown-item>
                </el-dropdown-menu>
              </template>
            </el-dropdown>
          </template>
        </el-table-column>

        <!-- Numbering column -->
        <el-table-column
          v-else-if="col === RSFindingsColumns.DEFAULT_NUMBERING"
          label="N°"
          width="120"
        >
          <template #default="{ row }">
            <el-input
              v-if="isEditing(row.id, 'defaultNumbering')"
              v-focus
              v-model="row.defaultNumbering"
              size="large"
              @blur="
                handleUpdate(row, 'defaultNumbering', row.defaultNumbering)
              "
              @keyup.enter="
                handleUpdate(row, 'defaultNumbering', row.defaultNumbering)
              "
              @click.stop
            />
            <span
              v-else
              class="editable-cell"
              @click.stop="startEditing(row.id, 'defaultNumbering')"
            >
              {{ row.defaultNumbering }}
            </span>
          </template>
        </el-table-column>

        <!-- Name column -->
        <el-table-column
          v-else-if="col === RSFindingsColumns.NAME"
          label="Désordre"
          min-width="150"
        >
          <template #default="{ row }">
            <el-input
              v-if="isEditing(row.id, 'name')"
              v-focus
              v-model="row.name"
              type="textarea"
              class="no-resize-textarea"
              :autosize="{ minRows: 2 }"
              @blur="handleUpdate(row, 'name', row.name)"
              @click.stop
            />
            <span
              v-else
              class="editable-cell textarea"
              @click.stop="startEditing(row.id, 'name')"
            >
              {{ row.name }}
            </span>
          </template>
        </el-table-column>

        <!-- Comment column -->
        <el-table-column
          v-else-if="col === RSFindingsColumns.COMMENT"
          label="Commentaire"
          min-width="200"
        >
          <template #default="{ row }">
            <el-input
              v-if="isEditing(row.id, 'comment')"
              v-focus
              v-model="row.comment"
              type="textarea"
              class="no-resize-textarea"
              :autosize="{ minRows: 2 }"
              @blur="handleUpdate(row, 'comment', row.comment)"
              @click.stop
            />
            <span
              v-else
              class="editable-cell textarea"
              :class="{ 'text-muted': !row.comment }"
              @click.stop="startEditing(row.id, 'comment')"
            >
              {{ row.comment }}
            </span>
          </template>
        </el-table-column>

        <!-- Location column -->
        <el-table-column
          v-else-if="col === RSFindingsColumns.LOCATION"
          label="Localisation"
          min-width="150"
        >
          <template #default="{ row }">
            <el-input
              v-if="isEditing(row.id, 'location')"
              v-focus
              v-model="row.location"
              @blur="handleUpdate(row, 'location', row.location)"
              @keyup.enter="handleUpdate(row, 'location', row.location)"
              @click.stop
            />
            <span
              v-else
              class="editable-cell"
              :class="{ 'text-muted': !row.location }"
              @click.stop="startEditing(row.id, 'location')"
            >
              {{ row.location }}
            </span>
          </template>
        </el-table-column>

        <!-- Evolution column -->
        <el-table-column
          v-else-if="col === RSFindingsColumns.EVOLUTION"
          label="Évolution"
          width="120"
          align="center"
        >
          <template #default="{ row }">
            <div class="d-flex align-items-center justify-content-center">
              <span
                class="cursor-pointer hoverable-white bordered rounded d-flex align-items-center justify-content-center"
                style="height: 30px; aspect-ratio: 1"
                :class="{
                  'bg-warning': row.evolution,
                }"
                @click.stop="handleUpdate(row, 'evolution', !row.evolution)"
              >
                <el-icon v-if="row.evolution" size="1.6rem"
                  ><i class="bi-check text-white"></i
                ></el-icon>
              </span>
            </div>
          </template>
        </el-table-column>

        <!-- Security column -->
        <el-table-column
          v-else-if="col === RSFindingsColumns.SECURITY"
          label="Sécurité"
          width="110"
          align="center"
        >
          <template #default="{ row }">
            <div class="d-flex align-items-center justify-content-center">
              <span
                class="cursor-pointer hoverable-white bordered rounded d-flex align-items-center justify-content-center"
                style="height: 30px; aspect-ratio: 1"
                :class="{
                  'bg-warning': row.security,
                }"
                @click.stop="handleUpdate(row, 'security', !row.security)"
              >
                <el-icon v-if="row.security" size="1.6rem"
                  ><i class="bi-check text-white"></i
                ></el-icon>
              </span>
            </div>
          </template>
        </el-table-column>

        <!-- Photos column -->
        <el-table-column
          v-else-if="col === 'photos'"
          label="Photos"
          width="180"
          align="left"
        >
          <template #default="{ row }">
            <div class="d-flex gap-1 align-items-center justify-content-start">
              <DRBtnIcon
                icon="bi-plus-lg"
                variant="outline"
                :size="32"
                @click.stop="openGalleryForFinding(row)"
              />
              <template v-if="row.reportImages?.length">
                <div
                  v-for="(img, index) in row.reportImages.slice(0, 2)"
                  :key="img.id"
                  class="shadow-1 cursor-pointer"
                  @click.stop="openImageViewer(row, Number(index))"
                >
                  <VisualiseImage
                    :id="img.diagnosticImage?.fileId"
                    class="finding-thumbnail"
                    fill
                    hide-preview
                  />
                </div>
                <span
                  v-if="row.reportImages.length > 2"
                  class="text-dark cursor-pointer"
                  @click.stop="openImageViewer(row, 2)"
                >
                  +{{ row.reportImages.length - 2 }}
                </span>
              </template>
            </div>
          </template>
        </el-table-column>
      </template>

      <!-- Actions column (always last) -->
      <el-table-column width="80" align="right">
        <template #default="{ row }">
          <div class="d-flex gap-2 justify-content-center" @click.stop>
            <FindingActionsDropdown
              :finding="row"
              :report-id="reportId"
              :is-deleting="isDeleting"
              @delete="handleDeleteFinding(row)"
              @move="(sectionId) => handleMoveFinding(row, sectionId)"
              @edit="emit('edit', row)"
            />
          </div>
        </template>
      </el-table-column>
    </el-table>

    <!-- Add finding button -->
    <div
      v-if="showAddButton"
      class="d-flex justify-content-center py-3 border-top"
    >
      <DRBtn
        variant="outline"
        size="sm"
        icon="bi-plus-lg"
        :label="$t('report:findings.addFinding')"
        @click="emit('create')"
      />
    </div>
  </div>

  <!-- Image viewer -->
  <el-image-viewer
    v-if="showViewer"
    :url-list="viewerImages"
    :initial-index="viewerInitialIndex"
    hide-on-click-modal
    teleported
    @close="showViewer = false"
  />

  <!-- Gallery modal for adding images -->
  <GaleryModal
    v-model="showGallery"
    :diagnosticId="diagnosticId"
    :reportId="reportId"
    :loading="isAddingImage"
    :priority-section-id="sectionId ?? undefined"
    :exclude-finding-id="selectedFindingForGallery?.id"
    :title="$t('report:findings.selectPhoto')"
    :message="$t('report:findings.selectPhotoDesc')"
    :confirmLabel="$t('report:findings.addPhoto')"
    multiple
    @confirm="handleAddImages"
  />
</template>

<style lang="scss" scoped>
:deep(.el-table) {
  --el-table-border-color: var(--bs-border-color);
  --el-table-row-hover-bg-color: var(--bs-gray-100);

  .el-table__row {
    cursor: pointer;

    td {
      padding: 8px 4px;
      height: 1px; // Hack CSS pour permettre height:100% sur les enfants dans un <td>

      .cell {
        height: 100%;
        display: flex;
        flex-direction: column;
      }
    }
  }

  .el-table__header-wrapper th {
    padding: 12px 8px;
    background-color: var(--bs-gray-100);
  }
}

.finding-thumbnail {
  width: 60px;
  height: 45px;
  overflow: hidden;
  border-radius: 8px;
}

.editable-cell {
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 8px;
  background-color: var(--bs-white);
  border: 1px solid var(--bs-border-color);
  transition: background-color 0.15s;
  display: block;
  white-space: pre-line;
  word-break: break-word;
  min-height: 32px;

  &:hover {
    background-color: var(--bs-light);
  }

  &.textarea {
    flex: 1;
    min-height: 52px;
  }
}

:deep(.no-resize-textarea) {
  textarea {
    resize: none;
  }
}

.cursor-pointer {
  cursor: pointer;
}
</style>
